// src/pages/LicenseList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCopy,
  FiChevronLeft,
  FiChevronRight,
  FiX,
} from "react-icons/fi";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import TopInfoBar from "../components/TopInfoBar";
import { authHeader, handle401Error } from "../utils/auth";
import { API_BASE } from "../config";
import { logError } from "../utils/logger";
import { getUserFriendlyError } from "../utils/errorMessages";

/* ============== MAIN ============== */
export default function LicenseList() {
  const blue = "#3366CC";
  const text = "#0F172A";
  const navigate = useNavigate();
  const isLoggedIn =
    typeof window !== "undefined" && sessionStorage.getItem("isLoggedIn") === "true";

  // ====== STATE ======
  const [loading, setLoading] = useState(true);
  const [all, setAll] = useState([]); // licenses mapped for table
  const [companyName, setCompanyName] = useState(""); // show client org
  const [error, setError] = useState("");

  // ====== FILTERS ======
  const [status, setStatus] = useState("all"); // all | active | expired
  const [ltype, setLtype] = useState("all"); // all | subscription | trial
  const [exp, setExp] = useState("any"); // any | soon | expired
  const [copied, setCopied] = useState("");
  const [copyFail, setCopyFail] = useState("");

  // ====== HELPERS ======
  // ใช้ authHeader จาก utils/auth.js

  const daysRemaining = (d) => {
    if (!d) return Number.NEGATIVE_INFINITY;
    const end = new Date(d).setHours(0, 0, 0, 0);
    const now = new Date().setHours(0, 0, 0, 0);
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  };

  const toUiRow = (lic, org = "") => {
    // lic: { id, licenseKey, type(term), productSku, issuedAt, expiresAt, status }
    const remain = lic.expiresAt
      ? daysRemaining(lic.expiresAt)
      : Number.POSITIVE_INFINITY;

    const computedStatus =
      lic.expiresAt && remain <= 0
        ? "Expired"
        : lic.status?.toLowerCase() === "inactive"
        ? "Expired"
        : "Active";

    const product =
      lic.productSku && lic.productSku !== "-" ? lic.productSku : "SmartAudit";
    const fmt = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "-");

    return {
      id: lic.id,
      key: lic.licenseKey,
      product,
      status: computedStatus,
      issueDate: fmt(lic.issuedAt),
      expiryDate: fmt(lic.expiresAt),
      org: org || "—",
      type:
        (lic.type || "").charAt(0).toUpperCase() +
        (lic.type || "").slice(1), // Trial/Subscription/Perpetual
      raw: lic,
    };
  };

  // ====== FETCH DATA ======
  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        setError("");

        // 1) /api/me
        const meRes = await fetch(`${API_BASE}/me`, {
          headers: { "Content-Type": "application/json", ...authHeader() },
        });
        if (handle401Error(meRes, navigate)) {
          return;
        }
        if (!meRes.ok) {
          const err = await meRes.json().catch(() => ({}));
          throw new Error(err?.detail || "Failed to load user data.");
        }
        const me = await meRes.json();
        const clientId = me?.id;
        setCompanyName(me?.company || "");

        // 2) /api/licenses?clientId=...
        const licRes = await fetch(
          `${API_BASE}/licenses?clientId=${encodeURIComponent(clientId)}`,
          {
            headers: { "Content-Type": "application/json", ...authHeader() },
          }
        );
        if (!licRes.ok) {
          const err = await licRes.json().catch(() => ({}));
          throw new Error(err?.detail || "Failed to load licenses.");
        }
        const rawList = await licRes.json();

        const mapped = (rawList || []).map((x) =>
          toUiRow(
            {
              id: x.id,
              licenseKey: x.license_key || x.licenseKey,
              type: x.term || x.type,
              productSku: x.product_sku || x.productSku,
              issuedAt: x.issued_at || x.issuedAt,
              expiresAt: x.expires_at || x.expiresAt,
              status: x.status,
            },
            me?.company || ""
          )
        );

        setAll(mapped);
      } catch (e) {
        setError(getUserFriendlyError(e, "general"));
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== FILTER + PAGINATION ======
  const filtered = useMemo(() => {
    return all.filter((r) => {
      if (status !== "all" && r.status.toLowerCase() !== status) return false;
      if (ltype !== "all" && r.type.toLowerCase() !== ltype) return false;
      if (exp !== "any") {
        const remain = daysRemaining(r.expiryDate === "-" ? null : r.expiryDate);
        if (exp === "expired" && !(remain <= 0)) return false;
        if (exp === "soon" && !(remain > 0 && remain <= 30)) return false;
      }
      return true;
    });
  }, [all, status, ltype, exp]);

  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const rows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filtered.slice(start, end);
  }, [filtered, page]);

  // ====== UI HELPERS ======
  const copyKey = async (k) => {
    try {
      await navigator.clipboard.writeText(k);
      setCopied(k);
      setTimeout(() => setCopied(""), 2000);
    } catch (err) {
      logError("Failed to copy!", err);
      setCopyFail(k);
      setTimeout(() => setCopyFail(""), 2500);
    }
  };

  const pageList = useMemo(() => {
    const max = 7;
    if (totalPages <= max)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const show = new Set(
      [1, 2, totalPages, totalPages - 1, page, page - 1, page + 1].filter(
        (p) => p >= 1 && p <= totalPages
      )
    );
    const arr = Array.from(show).sort((a, b) => a - b);
    const withDots = [];
    for (let i = 0; i < arr.length; i++) {
      withDots.push(arr[i]);
      if (i < arr.length - 1 && arr[i + 1] !== arr[i] + 1) withDots.push("…");
    }
    return withDots;
  }, [page, totalPages]);

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));
  const prevDisabled = page <= 1;
  const nextDisabled = page >= totalPages;

  // ====== STYLE ======
  const container = {
    width: "100%",
    background: "#fff",
    overflow: "visible",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
  };

  const responsiveCSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; }
  .page-container { width: 100%; overflow-x: hidden; background: #fff; }

  .table-responsive-wrapper {
    width: 100%;
    overflow-x: auto; /* เปิดไว้เผื่อจอเล็กกว่า 1000px ใน Tablet */
    -webkit-overflow-scrolling: touch;
    background: #fff;
  }

  .desktop-table-container {
    width: 100%;
    min-width: 900px; /* ลดลงมาเพื่อให้พอดีกับหน้าจอ Laptop ส่วนใหญ่ */
  }

  .desktop-table-header, .desktop-table-row {
    display: grid !important;
    /* แบ่งสัดส่วน: Status(120px) | Key(2.5เท่า) | Product(1.2เท่า) | Date(1.2เท่า) | Action(120px) */
    grid-template-columns: 120px 2.5fr 1.2fr 1.2fr 120px;
    gap: 16px;
    align-items: center;
  }

  .desktop-table-header > div, .desktop-table-row > div {
    padding: 0 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .action-col {
    text-align: right;
    display: flex;
    justify-content: flex-end;
  }

  .desktop-table-header > div:last-child, 
  .desktop-table-row > div:last-child {
    text-align: right;
    justify-content: flex-end;
    display: flex;
  }
  
  .key-display-box {
    display: flex;
    align-items: center;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 4px 10px;
    borderRadius: 8px;
    gap: 8px;
    width: fit-content;
    max-width: 100%;
  }

  .filter-container {
    padding: 24px 20px;
    display: grid;
    grid-template-columns: repeat(3, 190px); /* ล็อคความกว้าง dropdown ให้เท่ากัน */
    gap: 16px;
    justify-content: end;
    align-items: end;
  }
  .filter-section {
    padding: 24px 20px;
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-end; /* Desktop: เรียงขวา */
  }
   
  .filter-group {
    flex: 0 1 200px; /* Desktop: ล็อคความกว้างเริ่มต้น */
  }


  .mobile-license-card { display: none !important; }

  
  @media (max-width: 1200px) { 
    .page-container { padding: 0; } 
  }

  @media (max-width: 1024px) {
    .filter-container {
      grid-template-columns: repeat(2, 1fr) !important; /* Tablet: เรียง 2 แถว */
      justify-content: stretch !important;
    }
  }

  @media (max-width: 768px) {
    .top-info-bar { display: none !important; }
    .desktop-nav { display: none !important; }
    .hamburger-btn { display: inline-flex !important; }
    .sticky-nav { height: 60px !important; padding: 0 16px !important; }

    .desktop-table-header, .desktop-table-row { display: none !important; }
    .table-responsive-wrapper { overflow-x: visible; }
    .desktop-table-container { min-width: 100% !important; }

    .mobile-license-card {
      display: block !important;
      padding: 20px;
      border-bottom: 1px solid #E5E7EB;
      background: #fff;
    }

    .filter-container {
      grid-template-columns: 1fr !important;
      padding: 16px !important;
      gap: 12px !important;
    }
      
    .filter-label { font-size: 13px !important; }
    .hero-title { font-size: 28px !important; }

    .filter-section {
      padding: 16px;
      display: grid;
      grid-template-columns: 1fr; 
      gap: 12px;
    }

    .filter-group {
      max-width: 100%;
    }
  }

  [id] { scroll-margin-top: 70px; }
`;

/* ============== SUB-COMPONENT: StatusBadge ============== */
function StatusBadge({ status }) {
  const isActive = status === "Active";
  return (
    <span
      style={{
        padding: "4px 12px",
        borderRadius: 999,
        background: isActive ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)",
        color: isActive ? "#16A34A" : "#EF4444",
        fontWeight: 700,
        fontSize: 12,
        whiteSpace: "nowrap",
        display: "inline-block",
      }}
    >
      {status}
    </span>
  );
}


  return (
    <>
      <style>{responsiveCSS}</style>
      <div style={container}>
        {/* Sticky header */}
        <div style={{ position: "sticky", top: 0, zIndex: 1000, background: "#fff" }}>
          <TopInfoBar blue={blue} />
          <Navbar text={text} isLoggedIn={isLoggedIn} />
        </div>

        {/* Hero */}
        <section
          style={{ background: "#0E2B55", color: "#fff", padding: "28px 24px 30px" }}
        >
          <div style={{ maxWidth: 1120, margin: "0 auto" }}>
            <h1
              style={{
                margin: 0,
                fontSize: 36,
                fontWeight: 800,
                letterSpacing: 0.2,
              }}
            >
              LICENSE LIST
            </h1>
            {!!companyName && (
              <p style={{ margin: "8px 0 0", color: "#93c5fd" }}>
                Organization: <b>{companyName}</b>
              </p>
            )}
            <p
              style={{
                margin: "10px 0 0",
                color: "#2BB0F6",
                opacity: 0.95,
                lineHeight: 1.55,
              }}
            >
              View all licenses associated with your account.
            </p>
          </div>
        </section>

        {/* Table + filters */}
        <section style={{ background: "#F3F6FB", padding: "42px 16px 80px" }}>
          <div
            style={{
              maxWidth: 1120,
              margin: "0 auto",
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: 16,
            }}
          >
            {/* Filters */}
            <div className="filter-section">
              <div className="filter-group">
                <Filter label="Status">
                  <select
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    style={selectCompact}
                  >
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="expired">Expired</option>
                  </select>
                </Filter>
              </div>

              <div className="filter-group">
                <Filter label="License Type">
                  <select
                    value={ltype}
                    onChange={(e) => { setLtype(e.target.value); setPage(1); }}
                    style={selectCompact}
                  >
                    <option value="all">All Types</option>
                    <option value="trial">Trial</option>
                    <option value="purchase">Purchase</option>
                  </select>
                </Filter>
              </div>

              <div className="filter-group">
                <Filter label="Expiration Date">
                  <select
                    value={exp}
                    onChange={(e) => { setExp(e.target.value); setPage(1); }}
                    style={selectCompact}
                  >
                    <option value="any">Any</option>
                    <option value="soon">Expiring in 30 days</option>
                    <option value="expired">Expired</option>
                  </select>
                </Filter>
              </div>
            </div>

            {/* Error / Loading */}
            {loading && (
              <div 
                role="status"
                aria-live="polite"
                aria-label="Loading license list"
                style={{ padding: 40, textAlign: "center", color: "#64748B" }}
              >
                Loading...
              </div>
            )}
            {!!error && !loading && (
              <div
                role="alert"
                aria-live="polite"
                style={{
                  marginBottom: 20,
                  borderRadius: 10,
                  border: "1px solid #fecaca",
                  background: "#fef2f2",
                  padding: "12px 16px",
                  color: "#b91c1c",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: "0 24px",
                }}
              >
                <FiX size={16} /> {error}
              </div>
            )}

            {/* Table Section */}
            {!loading && !error && (
              <>
                {/* 1. Desktop View Wrapper (Scrollable if screen too small) */}
                <div className="table-responsive-wrapper">
                  <div className="desktop-table-container">
                    {/* Header (Desktop Only) */}
                    <div className="desktop-table-header"
                      style={{
                        padding: "14px 20px", background: "#F8FAFC", borderBottom: "2px solid #F1F5F9", fontWeight: 700
                      }}
                    >
                      <div>Status</div>
                      <div>License Key</div>
                      <div>Product</div>
                      <div>Expiration Date</div>
                      <div style={{ textAlign: 'right' }}>Action</div>
                    </div>

                    {/* Desktop Rows */}
                    {rows.length === 0 ? (
                      <div className="desktop-table-row" style={{ padding: 32, textAlign: 'center', color: "#64748B", display: 'block' }}>
                        No licenses found.
                      </div>
                    ) : (
                      rows.map((r) => (
                        <div className="desktop-table-row"
                          key={`desktop-${r.id}`}
                          style={{
                            padding: "12px 20px", borderBottom: "1px solid #F1F5F9",
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "#F8FAFC"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                          <div><StatusBadge status={r.status} /></div>

                          <div style={{ overflow: 'visible' }}>
                            <div className="key-display-box">
                              <code style={{ fontSize: 13, color: '#334155', fontFamily: 'monospace' }}>{r.key}</code>
                              <button
                                onClick={() => copyKey(r.key)}
                                title="Copy Key"
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex',
                                  color: copied === r.key ? '#16A34A' : '#94A3B8', transition: 'all 0.2s'
                                }}
                              >
                                <FiCopy size={14} />
                              </button>
                              {copied === r.key && <span style={{ fontSize: 10, color: '#16A34A', fontWeight: 600 }}>Copied!</span>}
                              {copyFail === r.key && <span style={{ fontSize: 10, color: '#DC2626', fontWeight: 600 }}>Failed!</span>}
                            </div>
                          </div>

                          <div style={{ fontSize: 14 }}>{r.product}</div>
                          <div style={{ fontSize: 14 }}>{r.expiryDate}</div>
                          <div style={{ textAlign: 'right' }}>
                            <button 
                              onClick={() => {
                                // เก็บแค่ licenseKey เพื่อความปลอดภัย แทนการเก็บทั้ง object
                                sessionStorage.setItem("selectedLicenseKey", r.key);
                                navigate("/view");
                              }} 
                              style={linkBtn}
                            >
                              Details
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* 2. Mobile View (Card Layout - Hidden on Desktop via CSS) */}
                <div className="mobile-cards-container">
                  {rows.length === 0 ? (
                    <div style={{ padding: 32, textAlign: 'center', color: "#64748B" }}>No licenses found.</div>
                  ) : (
                    rows.map((r) => (
                      <div className="mobile-license-card" key={`mobile-${r.id}`}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                          <StatusBadge status={r.status} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>{r.product}</span>
                        </div>
                        
                        <div style={{ background: '#F8FAFC', padding: '12px', borderRadius: '10px', marginBottom: 12, border: '1px solid #E2E8F0' }}>
                          <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4, textTransform: 'uppercase' }}>License Key</div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <code style={{ fontSize: 13, wordBreak: 'break-all', flex: 1, color: '#1E293B' }}>{r.key}</code>
                            <button
                              onClick={() => copyKey(r.key)}
                              style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer' }}
                              title={copied === r.key ? "Copied!" : copyFail === r.key ? "Copy failed" : "Copy to clipboard"}
                            >
                              <FiCopy
                                size={16}
                                style={{ color: copied === r.key ? "#16A34A" : copyFail === r.key ? "#DC2626" : "#3366CC" }}
                              />
                            </button>
                          </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 }}>
                          <div style={{ color: '#475569' }}>
                            <span style={{ fontSize: 12, color: '#94A3B8' }}>Expires: </span>
                            <b>{r.expiryDate}</b>
                          </div>
                          <button 
                            onClick={() => {
                              // เก็บแค่ licenseKey เพื่อความปลอดภัย แทนการเก็บทั้ง object
                              sessionStorage.setItem("selectedLicenseKey", r.key);
                              navigate("/view");
                            }} 
                            style={linkBtn}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* 3. Pagination (Centered for both views) */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                    padding: "24px 16px",
                    borderTop: "1px solid #F1F5F9"
                  }}
                >
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      aria-label="Previous page"
                      onClick={() => goTo(page - 1)}
                      disabled={prevDisabled}
                      style={{ ...iconBtn, ...(prevDisabled ? iconBtnDisabled : {}) }}
                    >
                      <FiChevronLeft />
                    </button>

                    {pageList.map((p, idx) =>
                      p === "…" ? (
                        <span key={`dots-${idx}`} style={{ padding: "0 6px", color: "#94A3B8" }}>…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => goTo(p)}
                          aria-label={p === page ? `Current page, page ${p}` : `Go to page ${p}`}
                          aria-current={p === page ? "page" : undefined}
                          style={p === page ? pageBtnActive : pageBtn}
                        >
                          {p}
                        </button>
                      )
                    )}

                    <button
                      aria-label="Next page"
                      onClick={() => goTo(page + 1)}
                      disabled={nextDisabled}
                      style={{ ...iconBtn, ...(nextDisabled ? iconBtnDisabled : {}) }}
                    >
                      <FiChevronRight />
                    </button>
                  </div>

                  <div style={{ color: "#64748B", fontSize: 13 }}>
                    Page {page} of {totalPages} | {filtered.length} Result{filtered.length !== 1 ? "s" : ""}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}

/* ============== small helpers / shared bits ============== */
function Filter({ label, children }) {
  return (
    <label
      style={{
        display: "grid",
        gap: 6,
        color: "#0F172A",
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {label}
      {children}
    </label>
  );
}

// const select = {
//   height: 36,
//   borderRadius: 8,
//   border: "1px solid #CBD5E1",
//   padding: "0 10px",
//   background: "#fff",
// };

const selectCompact = {
  width: "100%", 
  height: 42, 
  borderRadius: 10,
  border: "1px solid #CBD5E1",
  padding: "0 35px 0 12px", 
  background: "#fff",
  fontSize: 14,
  color: "#334155",
  cursor: "pointer",
  appearance: "none", 
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748B' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
  backgroundSize: "18px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease",
};

const iconBtn = {
  width: 28,
  height: 28,
  borderRadius: 8,
  border: "1px solid #CBD5E1",
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const iconBtnDisabled = {
  background: "#F1F5F9",
  border: "1px solid #E5E7EB",
  color: "#94A3B8",
  cursor: "not-allowed",
  opacity: 0.7,
};

const pageBtn = {
  minWidth: 28,
  height: 28,
  borderRadius: 8,
  padding: "0 8px",
  border: "1px solid #E2E8F0",
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 600,
  cursor: "pointer",
};

const pageBtnActive = {
  ...pageBtn,
  background: "#E2E8F0",
  borderColor: "#CBD5E1",
  fontWeight: 700,
};

const linkBtn = {
  background: "transparent",
  border: "none",
  color: "#2563EB",
  fontWeight: 700,
  cursor: "pointer",
};

